import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, X } from "lucide-react";
import { useUI } from "@/context/UIContext";
import { useProducts } from "@/hooks/useCatalog";
import { PopularSearches, SearchResults } from "@/components/search/SearchResults";

function useDebouncedTerm(term, delay = 250) {
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), delay);
    return () => clearTimeout(t);
  }, [term, delay]);
  return debounced;
}

export function SearchModal() {
  const { searchOpen, setSearchOpen } = useUI();
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const debounced = useDebouncedTerm(term);

  useEffect(() => {
    if (!searchOpen) setTerm("");
  }, [searchOpen]);

  const enabled = debounced.length >= 2;
  const { data, isFetching } = useProducts({ search: debounced, limit: 6 }, { enabled });
  const results = enabled ? data?.items || [] : [];

  const submit = (q) => {
    if (!q) return;
    setSearchOpen(false);
    navigate(`/shop?search=${encodeURIComponent(q)}`);
  };

  return (
    <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
      <DialogContent
        className="top-0 max-w-2xl translate-y-0 gap-0 border-border bg-ivory p-0 sm:rounded-none [&>button]:hidden data-[state=open]:slide-in-from-top-4"
        data-testid="search-modal"
      >
        <DialogTitle className="sr-only">Search products</DialogTitle>
        <DialogDescription className="sr-only">Search the ÉLORA jewellery catalogue</DialogDescription>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(term.trim());
          }}
          className="flex items-center gap-3 border-b border-border px-5 py-4"
        >
          <Search className="h-5 w-5 text-gold" />
          <input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search for necklaces, rings, pearls…"
            data-testid="search-input-field"
            className="flex-1 bg-transparent text-base text-ink placeholder:text-muted-foreground focus:outline-none"
          />
          <button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search" className="text-ink hover:text-gold">
            <X size={20} />
          </button>
        </form>

        <div className="max-h-[60vh] overflow-y-auto p-5">
          {enabled ? (
            <SearchResults results={results} term={debounced} isFetching={isFetching} onSelect={() => setSearchOpen(false)} onViewAll={() => submit(debounced)} />
          ) : (
            <PopularSearches onPick={submit} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
